import React from 'react'
import { connect } from 'react-redux'

import { RouteComponentProps } from 'react-router'
import { withRouter } from 'react-router-dom'
import { isProfilingSpace } from './app/context'
import { Entity } from './app/entity'
import Navigator from './app/navigator'
import ApplicationComponent from './dashboard/application'
import Link from './helper/link'
import Loading from './helper/loading'
import MomentDate from './momentDate'
import { ApplicationState } from './redux/ApplicationState'
import { DashboardEntity, DashboardOrganization } from './redux/dashboard/DashboardState'
import { Box, BoxBody, BoxHeader, BoxTitle } from './ui/box'
import { Col, Row } from './ui/grid'
import ErroPage404 from './ErrorPage404'
import { Alert } from './ui/alert'
import { Application } from './domain/Application'
import { User } from './domain/User'

interface StateProps {
    navigator: Navigator
    user: User | null
    parameters: { [key: string]: string }
    data: DashboardEntity | null
}

type Props = StateProps & RouteComponentProps

interface State {
    organizationFilter: string
}

class Dashboard extends React.Component<Props, State> {
    public static displayName = 'Dashboard'

    public static defaultProps = {
        data: null,
    }

    public state: State = {
        organizationFilter: '',
    }

    public handleOrganizationFilterChange = ev => {
        this.setState({ organizationFilter: ev.target.value })
    }

    public render() {
        if (!this.props.data) {
            return <Loading height={90} />
        }

        const { navigator: router } = this.props

        let organizations = Object.values(this.props.data.organizations)

        const emptyOrInvalidCurrentParameter = organizations.find(organization => organization.isCurrent) == undefined

        if (organizations.length === 0) {
            if (this.props.data.openInvitations > 0) {
                window.location.replace(router.path('XhprofOrganizationBundle.Members.invitations', {}))
            }

            window.location.replace(router.path('XhprofOrganizationBundle.Organization.create', { welcome: true }))
            return
        }

        if (this.state.organizationFilter) {
            organizations = organizations.filter(organization =>
                organization.name.toLowerCase().includes(this.state.organizationFilter.toLowerCase()),
            )
        }

        // Move the current organization to the top of the selection dropdown
        if (this.props.parameters.current) {
            const currentOrganization = organizations.find(organization => this.props.parameters.current)

            if (currentOrganization) {
                organizations = organizations.filter(organization => organization.name !== currentOrganization.name)
                organizations.unshift(currentOrganization)
            }
        }

        organizations = organizations.filter(organization => organization.isCurrent)

        if (emptyOrInvalidCurrentParameter) {
            return (
                <div className="dashboard">
                    <ErroPage404 />
                </div>
            )
        }

        return (
            <div className="dashboard">
                {this.renderInvitations()}
                <Row>{organizations.map(this.renderOrganization)}</Row>
            </div>
        )
    }

    private renderOrganization = (organization: DashboardOrganization): React.ReactNode => {
        if (!organization.isCurrent) {
            return (
                <Col key={organization.id} widthLarge={3}>
                    <Box>
                        <BoxHeader>
                            <BoxTitle>{organization.name}</BoxTitle>

                            <div className="box-tools pull-right" style={{ paddingTop: '10px' }}>
                                <Link route="dashboard" params={{ current: organization.name }}>
                                    <i className="fa fa-arrow-circle-o-right fa-2x" />
                                </Link>
                            </div>
                        </BoxHeader>
                    </Box>
                </Col>
            )
        }

        const createApplicationRoute = organization.isTideways6
            ? 'XhprofOrganizationBundle.Application.createTideways6'
            : 'XhprofOrganizationBundle.Application.create'

        const normalApplications = Object.values(organization.applications || {}).filter(application => !isProfilingSpace(application))
        const profilingApplications = Object.values(organization.applications || {}).filter(application => isProfilingSpace(application))

        return (
            <Col key={organization.id}>
                {this.renderTrialNote(organization)}
                <Box>
                    <BoxHeader>
                        <BoxTitle>{organization.name}</BoxTitle>
                        <div className="box-tools pull-right">
                            <span>
                                {!this.props.user ? null : this.props.user && this.props.user.defaultOrganization == organization.name ? (
                                    <span
                                        className="tw-tooltip tw-tooltip-dataText tw-tooltip--bottom"
                                        data-tooltip-text={'This is the default organization which is visible right after logging in'}
                                        style={{ paddingTop: '0', display: 'inline-block' }}
                                    >
                                        <Link className="btn btn-default tw-mr-16">
                                            <i className="fa fa-star" />
                                        </Link>
                                    </span>
                                ) : (
                                    <form method="POST" action="/settings/choose-default-organization" style={{ display: 'inline-block' }}>
                                        <span
                                            className="tw-tooltip tw-tooltip-dataText tw-tooltip--bottom"
                                            data-tooltip-text={'Mark as the default organization that is visible after logging in'}
                                            style={{ paddingTop: '0', display: 'inline-block' }}
                                        >
                                            <input name="organization" type="hidden" value={organization.name} />
                                            <button type="submit" className="btn btn-default tw-mr-16">
                                                <i className="fa fa-star-o" />
                                            </button>
                                        </span>
                                    </form>
                                )}
                                {!organization.isLockedForSubscriptionChanges ? (
                                    <Link
                                        className="btn btn-default tw-mr-16"
                                        route={createApplicationRoute}
                                        params={{ organization: organization.name }}
                                    >
                                        <i className="fa fa-plus-square" /> Create New Project
                                    </Link>
                                ) : null}
                                <Link
                                    className="btn btn-default tw-mr-16"
                                    route="XhprofOrganizationBundle.Members.show"
                                    params={{ organization: organization.name }}
                                >
                                    <i className="fa fa-users" /> Invite your team
                                </Link>
                                <Link
                                    route="XhprofOrganizationBundle.Members.show"
                                    params={{ organization: organization.name }}
                                    className="btn btn-default"
                                >
                                    <i className="fa fa-cog text-muted" /> Settings
                                </Link>
                            </span>
                        </div>
                    </BoxHeader>

                    {organization.checkTeamAccess && !organization.userHasTeamAccess && !organization.isAdmin ? (
                        <BoxBody className="centered">
                            <Alert level="warning">
                                <big>
                                    This organization is team based, but you have not been assigned to a team yet. Please contact one of the
                                    admins of this organization: {organization.adminUsers}
                                </big>
                            </Alert>
                        </BoxBody>
                    ) : null}
                    {!normalApplications && organization.isAdmin ? (
                        <BoxBody className="centered">
                            <p>
                                <big>No projects created yet in this organization</big>
                            </p>

                            <p>
                                <Link
                                    route={createApplicationRoute}
                                    params={{ organization: organization.name }}
                                    className="btn btn-lg btn-primary"
                                >
                                    Create New Project
                                </Link>
                            </p>
                        </BoxBody>
                    ) : null}
                    {!normalApplications && !organization.isAdmin ? (
                        <BoxBody className="centered">
                            <p>
                                <big>No projects visible for you in this organization.</big>
                            </p>
                        </BoxBody>
                    ) : null}
                    {normalApplications.map(application =>
                        this.props.data ? (
                            <ApplicationComponent
                                key={'org:' + organization.id + '_app:' + application.id}
                                navigator={this.props.navigator}
                                organization={organization}
                                application={application}
                                data={this.props.data.applications[application.id]}
                                incidents={this.props.data.incidents[application.id]}
                            />
                        ) : null,
                    )}
                </Box>
                {organization.hasProfilerApplication ? <Box>{this.renderProfilingSpaces(profilingApplications, organization)}</Box> : null}
            </Col>
        )
    }

    private renderTrialNote(organization: DashboardOrganization) {
        if (!organization.isTrial) {
            return null
        }

        if (organization.isDisabled) {
            return (
                <Row>
                    <Col width={8} centered>
                        <div className="text-center alert alert-danger tw-font-size-18">
                            <p>
                                <i className="fa fa-warning" /> Your Tideways trial period has ended.{' '}
                                <Link
                                    style={{ color: '#fff' }}
                                    route="XhprofBillingBundle.Checkout.start"
                                    params={{ organization: organization.name }}
                                >
                                    Subscribe
                                </Link>{' '}
                                to continue using Tideways.
                            </p>
                        </div>
                    </Col>
                </Row>
            )
        }

        // Don't show a notification if the trial is going for more than 1 year (Dev, Free accounts, etc.)
        if (new Date(organization.activeUntil.date).getTime() - new Date().getTime() > 365 * 24 * 60 * 60 * 1000) {
            return null
        }

        return (
            <Row>
                <Col width={8} centered>
                    <div className="text-center alert alert-warning tw-font-size-16">
                        <p>
                            <i className="fa fa-info-circle" /> Your Tideways trial period ends on{' '}
                            <MomentDate date={organization.activeUntil.date} displayFormat={'DD.MM.YYYY'} />.{' '}
                            <Link route="XhprofBillingBundle.Checkout.start" params={{ organization: organization.name }}>
                                Subscribe
                            </Link>{' '}
                            to continue using Tideways after this date.
                        </p>
                    </div>
                </Col>
            </Row>
        )
    }

    private renderInvitations() {
        if (!this.props.data) {
            return null
        }
        return (
            <>
                {this.props.data.openInvitations === 0 ? null : (
                    <Row>
                        <Col width={6} centered>
                            <div className="text-center alert alert-hint">
                                <p>
                                    <i className="fa fa-info-circle" /> You have{' '}
                                    <Link route="XhprofOrganizationBundle.Members.invitations">
                                        {this.props.data.openInvitations} open invitations
                                    </Link>{' '}
                                    to organizations that you should respond to.
                                </p>
                            </div>
                        </Col>
                    </Row>
                )}
            </>
        )
    }

    private renderProfilingSpaces = (profilingApplications: Application[], organization: DashboardOrganization) => (
        <>
            <BoxHeader>
                <BoxTitle>Profiling Spaces</BoxTitle>
                <div className="box-tools pull-right">
                    <Link
                        className="btn btn-default"
                        route="XhprofOrganizationBundle.Application.create-profiler"
                        params={{ organization: organization.name }}
                        style={{ marginRight: '20px' }}
                    >
                        <i className="fa fa-plus-square" /> Create New Profiling Space
                    </Link>
                </div>
            </BoxHeader>
            <BoxBody>
                {profilingApplications.map(application =>
                    this.props.data ? (
                        <ApplicationComponent
                            key={'org:' + organization.id + '_app:' + application.id}
                            navigator={this.props.navigator}
                            organization={organization}
                            application={application}
                            data={this.props.data.applications[application.id]}
                            incidents={this.props.data.incidents[application.id]}
                        />
                    ) : null,
                )}
                {profilingApplications.length === 0 ? (
                    <p className="centered">
                        This organization has no profiling spaces yet.{' '}
                        <Link route="XhprofOrganizationBundle.Application.create-profiler" params={{ organization: organization.name }}>
                            Create the first profiling space
                        </Link>
                        .
                    </p>
                ) : null}
            </BoxBody>
        </>
    )
}

export default withRouter(
    connect<StateProps>((globalState: ApplicationState, props: RouteComponentProps) => {
        const dashboard = globalState.dashboard.dashboard || new Entity()

        return {
            ...props,
            navigator: new Navigator(globalState.tidewaysContext.route),
            user: globalState.tidewaysContext.router.user,
            parameters: globalState.tidewaysContext.route.parameters,
            ...dashboard,
        }
    })(Dashboard),
)
